import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  BLOCK_INSET_HEIGHT_PX,
  BLOCK_INSET_TOP_PX,
  CourtColumnModel,
  CourtColumnSegment,
} from '../../court-grid.model';
import { hasOverlapWithBlockedRanges } from '../../court-grid-blocked-ranges.util';
import { minutesBetween } from '../../court-grid-time.util';
import {
  isCourseClickable,
  segmentDisplayText,
  segmentKindClass,
  segmentLabel,
  segmentTitle,
} from '../../court-segment-label.util';
import { VenueTimezoneService } from '../../../../../service/venue-timezone.service';
import { AggregatedCourseSessionDto } from '../../../../../service/court-availability-service';

/** A single interval-aligned, clickable free-slot cell — derived on the fly, never stored. */
interface FreeCellVm {
  startTimeUtc: string;
  top: number;
  height: number;
  outOfLeadTime: boolean;
}

/** Payload emitted when the user activates a free cell (click/tap or keyboard). */
export interface LanesFreeCellActivatedEvent {
  dateStr: string;
  courtId: string;
  courtName: string;
  startTimeUtc: string;
}

/** Payload emitted on mouse-pointerdown on a free cell — starts a
 *  press-drag-release selection gesture (the root owns the drag session). */
export interface LanesFreeCellDragStartEvent extends LanesFreeCellActivatedEvent {
  pointerEvent: PointerEvent;
}

/** Pointerdown on the selection block's top or bottom handle. */
export interface LaneResizeStartEvent {
  kind: 'resize-top' | 'resize-bottom';
  pointerEvent: PointerEvent;
}

/**
 * The active selection as rendered INSIDE this lane (design "Vertikale
 * Lanes": the selection block is a child of the lane, not a grid-level
 * overlay). null when the selection lives on another court/day.
 */
export interface LaneSelectionVm {
  startTimeUtc: string;
  durationMinutes: number;
  label: string;
  /** A drag (create/move/resize) is in flight — disables transitions and
   *  makes the block hit-test-transparent so elementFromPoint sees the lane. */
  dragging: boolean;
  /** The in-flight drag position is not bookable — renders the red state. */
  invalid: boolean;
}

/**
 * Dumb leaf of app-court-lanes-grid: one court's rounded, tinted lane for one
 * day, per the "Platzbelegung · Optionen" design's Vertikale-Lanes frames.
 * The lane's continuous background IS the free surface; free cells are
 * transparent click targets carrying only the interval hairline; booked/
 * course/past segments reuse the exact visualization of the classic grid
 * (colors, type + time labels, "Ecke" own-booking badge). The selection block
 * renders inside the lane with a bottom resize handle — the root component
 * owns the actual drag session (see selectionResizeStarted).
 */
@Component({
  selector: 'app-lanes-court-column',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './lanes-court-column.component.html',
  styleUrls: ['./lanes-court-column.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LanesCourtColumnComponent {
  column = input.required<CourtColumnModel>();
  /** YYYY-MM-DD calendar date this lane belongs to (day-level, not on CourtColumnModel itself). */
  dateStr = input.required<string>();
  pxPerMinute = input.required<number>();
  intervalMinutes = input.required<number>();
  /** Cells starting after this instant render greyed-out and are not selectable. null = no limit. */
  maxBookableUtc = input<Date | null>(null);
  /** Week mode — tighter radii/insets for the narrow per-day lanes (design 1W vs 1). */
  compact = input(false);
  /** The active selection, when it belongs to THIS court+day (root passes null otherwise). */
  selection = input<LaneSelectionVm | null>(null);

  /** Emitted for every free-cell activation — the root decides validity (lead-time gating, etc). */
  freeCellActivated = output<LanesFreeCellActivatedEvent>();

  /** Emitted on mouse-pointerdown on a free cell — the root starts a
   *  press-drag-release create gesture (drag down = longer duration). */
  freeCellDragStarted = output<LanesFreeCellDragStartEvent>();

  /** Pointerdown on the selection block's top/bottom handle — the root runs the resize drag. */
  selectionResizeStarted = output<LaneResizeStartEvent>();

  /** Pointerdown on the selection block's body — the root runs the move drag. */
  selectionBodyPointerDown = output<PointerEvent>();

  /** Hit-test anchor for drag geometry resolution via document.elementFromPoint(). */
  gridColKey = computed(() => `${this.dateStr()}__${this.column().courtId}`);

  /**
   * Emitted when the user activates a clickable course block (click/Enter/
   * Space) — bubbled up unchanged to booking-container.onCourseSelected(),
   * which opens the shared course-enrollment modal. Only fires for course
   * segments that are not past and where the user is NOT already enrolled
   * (see isCourseClickable()).
   */
  courseSessionClicked = output<AggregatedCourseSessionDto>();

  laneHeightPx = computed(() => {
    const col = this.column();
    if (!col.dayStartUtc || !col.dayEndUtc) return 0;
    return minutesBetween(col.dayStartUtc, col.dayEndUtc) * this.pxPerMinute();
  });

  segments = computed(() => this.column().segments.filter(s => s.kind !== 'available'));

  freeCells = computed<FreeCellVm[]>(() => {
    const col = this.column();
    if (!col.dayStartUtc || !col.dayEndUtc) return [];

    const interval = this.intervalMinutes();
    const pxPerMin = this.pxPerMinute();
    const maxBookable = this.maxBookableUtc();
    const startMs = new Date(col.dayStartUtc).getTime();
    const endMs = new Date(col.dayEndUtc).getTime();
    const stepMs = interval * 60_000;

    const cells: FreeCellVm[] = [];
    for (let t = startMs; t + stepMs <= endMs; t += stepMs) {
      if (hasOverlapWithBlockedRanges(t, t + stepMs, col.blockedRanges)) continue;
      cells.push({
        startTimeUtc: new Date(t).toISOString(),
        top: ((t - startMs) / 60_000) * pxPerMin,
        height: interval * pxPerMin,
        outOfLeadTime: maxBookable != null && t > maxBookable.getTime(),
      });
    }
    return cells;
  });

  onFreeCellActivate(cell: FreeCellVm): void {
    this.freeCellActivated.emit({
      dateStr: this.dateStr(),
      courtId: this.column().courtId,
      courtName: this.column().courtName,
      startTimeUtc: cell.startTimeUtc,
    });
  }

  /**
   * Mouse only: on touch, pointerdown must stay free so the browser can pan
   * the scrollbox (free cells cover most of the grid and have no
   * touch-action:none) — touch users tap to select via the (click) handler
   * and adjust via the selection's handles. Out-of-lead-time cells never
   * start a gesture (matches the click path's gating).
   */
  onFreeCellPointerDown(event: PointerEvent, cell: FreeCellVm): void {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    if (cell.outOfLeadTime) return;
    this.freeCellDragStarted.emit({
      dateStr: this.dateStr(),
      courtId: this.column().courtId,
      courtName: this.column().courtName,
      startTimeUtc: cell.startTimeUtc,
      pointerEvent: event,
    });
  }

  freeCellAriaLabel(cell: FreeCellVm): string {
    const time = this.formatTime(cell.startTimeUtc);
    if (cell.outOfLeadTime) {
      return `${this.column().courtName}, ${time}, außerhalb der Vorlaufzeit`;
    }
    return `${this.column().courtName}, ${time}, frei`;
  }

  // ---- Selection block (inside-lane, see LaneSelectionVm) --------------------
  selectionTop(sel: LaneSelectionVm): number {
    return (
      minutesBetween(this.column().dayStartUtc, sel.startTimeUtc) * this.pxPerMinute() +
      BLOCK_INSET_TOP_PX
    );
  }

  selectionHeight(sel: LaneSelectionVm): number {
    return sel.durationMinutes * this.pxPerMinute() - BLOCK_INSET_HEIGHT_PX;
  }

  onResizeHandlePointerDown(event: PointerEvent, kind: 'resize-top' | 'resize-bottom'): void {
    event.preventDefault();
    event.stopPropagation();
    this.selectionResizeStarted.emit({ kind, pointerEvent: event });
  }

  onSelectionBodyPointerDown(event: PointerEvent): void {
    this.selectionBodyPointerDown.emit(event);
  }

  // ---- Segment rendering (identical visualization to the classic grid) -------
  /** Un-inset segment top — the coordinate-space value (minutes × pxPerMinute). */
  segTop(seg: CourtColumnSegment): number {
    return minutesBetween(this.column().dayStartUtc, seg.startTimeUtc) * this.pxPerMinute();
  }

  /** Un-inset segment height — also drives segmentTier(), which must see the
   *  full geometry (tier thresholds match the classic grid's pre-inset `h`). */
  segHeight(seg: CourtColumnSegment): number {
    return minutesBetween(seg.startTimeUtc, seg.endTimeUtc) * this.pxPerMinute();
  }

  /** Rendered block top: +1px visual inset (see BLOCK_INSET_TOP_PX). */
  segBlockTop(seg: CourtColumnSegment): number {
    return this.segTop(seg) + BLOCK_INSET_TOP_PX;
  }

  /** Rendered block height: -2px visual inset so back-to-back blocks don't touch. */
  segBlockHeight(fullHeight: number): number {
    return fullHeight - BLOCK_INSET_HEIGHT_PX;
  }

  /** Shared with the classic column — see court-segment-label.util. */
  segmentKindClass(seg: CourtColumnSegment): string {
    return segmentKindClass(seg);
  }

  segmentTier(heightPx: number): 'xs' | 'sm' | 'lg' {
    if (heightPx < 34) return 'xs';
    if (heightPx < 58) return 'sm';
    return 'lg';
  }

  /** Plan §6.4.3 type word ("Kurs" / "Turnier – X" / "Gesperrt" / "Belegt"). */
  segmentLabel(seg: CourtColumnSegment): string {
    return segmentLabel(seg);
  }

  /** The line rendered inside the block (adds "· Anmelden · 12/16" on a joinable event). */
  segmentText(seg: CourtColumnSegment): string {
    return segmentDisplayText(seg);
  }

  segmentTitle(seg: CourtColumnSegment): string {
    return segmentTitle(
      seg,
      this.column().courtName,
      this.formatRange(seg.startTimeUtc, seg.endTimeUtc),
    );
  }

  /** Accessible label for the "Ecke" own-booking corner badge. */
  ownBadgeLabel(seg: CourtColumnSegment): string {
    return seg.kind === 'course' ? 'Du bist angemeldet' : 'Deine Buchung';
  }

  /**
   * A course/event block is clickable (opens the enrollment modal); a
   * 'blocked' Platzsperre never is. See court-segment-label.util.
   */
  isCourseClickable(seg: CourtColumnSegment): boolean {
    return isCourseClickable(seg);
  }

  onCourseSegmentActivate(seg: CourtColumnSegment): void {
    if (!this.isCourseClickable(seg)) return;
    this.courseSessionClicked.emit(seg.courseSession!);
  }

  courseAriaLabel(seg: CourtColumnSegment): string {
    return `${this.segmentTitle(seg)} · Details & Anmeldung`;
  }

  formatRange(startTimeUtc: string, endTimeUtc: string): string {
    return `${this.formatTime(startTimeUtc)}–${this.formatTime(endTimeUtc)}`;
  }

  formatTime(isoInstant: string): string {
    try {
      return this.venueTimezone.formatTime(isoInstant);
    } catch {
      return '';
    }
  }

  constructor(private venueTimezone: VenueTimezoneService) {}
}
